import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "peach_media_favorites";

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function useFavorites() {
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);

  useEffect(() => {
    setFavoriteIds(readFavorites());
  }, []);

  const persist = useCallback((next: string[]) => {
    setFavoriteIds(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage failures
    }
  }, []);

  const isFavorite = useCallback((id: string) => favoriteIds.includes(id), [favoriteIds]);

  const toggleFavorite = useCallback(
    (id: string) => {
      const current = readFavorites();
      const next = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      persist(next);
      return next.includes(id);
    },
    [persist]
  );

  const removeFavorite = useCallback(
    (id: string) => {
      const next = readFavorites().filter((item) => item !== id);
      persist(next);
    },
    [persist]
  );

  return { favoriteIds, isFavorite, toggleFavorite, removeFavorite };
}

export function getFavoriteIds(): string[] {
  return readFavorites();
}